import React, { useEffect, useRef, useState } from 'react';
import { retryWaitText } from './runRules';

/**
 * 진행률 카드 (OcrHub 전용 — 공용 ProgressCard의 fork).
 * ② 분리+OCR, ⑤ AI 분석 — 데몬이 비동기로 처리하고 5초 폴링으로 읽는 단계가 같은 모양을 쓴다.
 *
 * 공용 ProgressCard는 done/total만 말해서, "지금 처리 중"과 "처리기가 아직 안 가져가 대기 중"이
 * 화면에서 완전히 같아 보였다. 두 API 모두 pending/processing을 이미 주고 있으므로 그대로 쓴다.
 *
 * 세 가지가 움직여 "살아 있음"을 말한다:
 *   1. 줄무늬 — processing > 0 일 때만 흐른다. 대기만 하는 동안은 정지한다(멈춤과 돎의 구분).
 *   2. "n초 전 갱신" — 폴링이 켜져 있는 동안 1초마다 올라간다. 진행 숫자가 한동안 그대로여도 화면은 산다.
 *   3. 남은 시간 — 이 세션에서 실제 완료 속도를 재서 추정한다. 표본이 생기기 전에는 말하지 않는다.
 *      한때 OCR만 '페이지당 15초'를 기준값으로 썼는데, 그 값의 출처는 FE-2 지시서 한 줄뿐이고
 *      저장소 안에 근거가 없다. 실측 아닌 수치를 남은 시간으로 내보이지 않는다.
 *
 * pending/processing이 null이면 진행률 조회 전(또는 순단)이다 — 카운트를 지어내지 않고 확인 중으로 표시한다.
 */

// 남은 시간은 추정이다. 1분 미만은 5초 단위, 그 이상은 분 단위로 뭉개 정밀도를 과장하지 않는다.
function formatEta(sec) {
  if (!Number.isFinite(sec) || sec <= 0) return null;
  if (sec < 60) return `약 ${Math.max(5, Math.round(sec / 5) * 5)}초`;
  return `약 ${Math.ceil(sec / 60)}분`;
}

function formatAgo(ms) {
  if (ms == null || !Number.isFinite(ms)) return null;
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 1) return '방금 갱신';
  if (s < 60) return `${s}초 전 갱신`;
  return `${Math.floor(s / 60)}분 전 갱신`;
}

function OcrProgressCard({
  title = 'OCR 진행률',
  unit = 'p',
  // 대기·처리 중이 없는데 남은 것이 있을 때 뭐라고 부를지. null이면 그 줄을 아예 표시하지 않는다 —
  // AI 분석의 남은 건수는 '실행하면 되는 것'이 아니라 '검수가 아직 안 끝난 것'일 수 있어 단정하지 않는다.
  notQueuedLabel = '아직 실행하지 않은 페이지',
  // 실측 표본이 없을 때 쓸 기준값(초/건). 기본은 없음 — 근거 있는 값을 아는 호출부만 준다.
  defaultSecPerItem = null,
  done = 0,
  total = 0,
  failed = 0,
  pending = null,
  processing = null,
  polling = false,
  updatedAt = null,
  // 과부하 자동 재시도 대기(OCR 전용 — ocr-status.retrying · next_retry_at, BE-20260925 §2-1).
  // 재시도 대기 쪽은 pending 에 들어 있고 2·5·10·20·30분 간격을 두어, 이것을 말하지 않으면 한 시간 가까이
  // 「대기 N」이 그대로라 멈춘 것처럼 보인다. 옛 서버·분석 카드는 주지 않는다(0/null → 종전 그대로).
  retrying = 0,
  nextRetryAt = null,
  className = '',
  // 카드 맨 아래에 덧붙일 것(선택) — 진행률과 같은 카드 안에 있어야 하는 상세.
  children = null,
}) {
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const countsKnown = pending != null && processing != null;
  const active = countsKnown && processing > 0; // 실제로 도는 중
  const waiting = countsKnown && processing === 0 && pending > 0; // 대기열에는 있는데 아직 안 가져감
  const remain = countsKnown ? pending + processing : 0;
  const notQueued = countsKnown && remain === 0 ? Math.max(0, total - done - failed) : 0;

  // 1초 시계 — 폴링이 켜져 있는 동안에만 돈다(끝난 뒤 불필요한 렌더를 만들지 않는다)
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!polling) return undefined;
    setNow(Date.now()); // 폴링 재개 시 멈춰 있던 시각을 먼저 따라잡는다
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [polling]);

  // 완료 속도 실측 — done이 처음 늘어난 순간을 원점으로 잡는다.
  // 마운트 시각을 원점으로 쓰면 처리 경계와 어긋나 첫 구간이 실제보다 길게 잡힌다.
  const sampleRef = useRef({ prev: null, t0: null, done0: 0 });
  const [secPerItem, setSecPerItem] = useState(null);
  useEffect(() => {
    const s = sampleRef.current;
    if (s.prev === null) {
      s.prev = done;
      return;
    }
    if (done > s.prev) {
      if (s.t0 === null) {
        s.t0 = Date.now();
        s.done0 = done;
      } else {
        setSecPerItem((Date.now() - s.t0) / 1000 / (done - s.done0));
      }
    } else if (done < s.prev) {
      // 재실행 등으로 카운트가 되감기면 표본을 버린다 — 낡은 속도로 남은 시간을 말하지 않는다
      s.t0 = null;
      s.done0 = 0;
      setSecPerItem(null);
    }
    s.prev = done;
  }, [done]);

  const perItem = secPerItem ?? defaultSecPerItem;
  const eta = active && perItem != null ? formatEta(remain * perItem) : null;
  const ago = polling ? formatAgo(updatedAt == null ? null : now - updatedAt) : null;

  const retryN = countsKnown ? Math.min(Number(retrying) || 0, pending) : 0;
  let statusText = null;
  if (!countsKnown) {
    if (polling) statusText = '진행 상태를 확인하는 중';
  } else if (active) {
    statusText =
      `처리 중 ${processing}${unit}` +
      (pending - retryN > 0 ? ` · 대기 ${pending - retryN}${unit}` : '') +
      (retryN > 0 ? ` · 과부하 재시도 대기 ${retryN}${unit}` : '') +
      (eta ? ` · 남은 시간 ${eta}` : '');
  } else if (waiting && retryN > 0) {
    // 대기 전부 또는 일부가 과부하 재시도 대기 — 멈춤이 아니라 서버가 시간을 두고 다시 시도하는 중이다
    const plain = pending - retryN;
    statusText = `${plain > 0 ? `대기 ${plain}${unit} · ` : ''}${retryWaitText(retryN, nextRetryAt, now)}`;
  } else if (waiting) {
    statusText = `대기 ${pending}${unit} · 처리 시작을 기다리는 중`;
  } else if (notQueuedLabel && notQueued > 0) {
    statusText = `${notQueuedLabel} ${notQueued}${unit}`;
  }

  return (
    <div className={`rounded-3xl bg-white p-4 shadow-lg ${className}`}>
      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        <span className="text-sm font-bold text-gray-900">{pct}%</span>
      </div>

      <div
        className="h-2 w-full overflow-hidden rounded-full bg-gray-200"
        role="progressbar"
        aria-label={title}
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`h-2 rounded-full transition-all duration-300 ${
            failed > 0 ? 'bg-yellow-500' : 'bg-blue-500'
          } ${active ? 'progress-stripes' : ''}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* 무엇을 하는 중인지 — 상태가 바뀔 때만 읽히도록 aria-live(매초 바뀌는 갱신 시각은 여기 넣지 않는다) */}
      {statusText && (
        <p
          className={`mt-2 text-xs ${active ? 'font-medium text-blue-600' : 'text-gray-500'}`}
          aria-live="polite"
        >
          {statusText}
        </p>
      )}

      <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
        <span>
          완료 {done.toLocaleString()} / {total.toLocaleString()}{unit}
          {failed > 0 && <span className="ml-1 text-red-500">· 실패 {failed}{unit}</span>}
        </span>
        {ago && (
          <span className="flex shrink-0 items-center gap-1 text-gray-400">
            {ago}
            <span className="inline-block size-1.5 rounded-full bg-blue-500 motion-safe:animate-pulse" />
          </span>
        )}
      </div>

      {/* 진행률을 '어느 것'까지 짚어 주는 것 — ⑤가 분석 지도를 여기 넣는다.
          숫자와 같은 카드 안에 있어야 「완료 16/22건」과 격자가 한 사실의 두 표현으로 읽힌다.
          위에 선을 긋는 것은 카드 안에서 요약과 상세를 가르기 위해서다. */}
      {children && <div className="mt-3 border-t border-gray-100 pt-3">{children}</div>}
    </div>
  );
}

export default OcrProgressCard;
